import React from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  Ellipsis,
  Info,
  Monitor,
  PlusSquare,
  Share,
  Sparkles,
  TabletSmartphone,
} from "lucide-react";
import iosPwaStep1 from "@/assets/pwa/pwa_ios_1.jpeg";
import iosPwaStep2 from "@/assets/pwa/pwa_ios_2.jpeg";
import iosPwaStep3 from "@/assets/pwa/pwa_ios_3.jpeg";
import iosPwaStep4 from "@/assets/pwa/pwa_ios_4.jpeg";
import iosChromeStep1 from "@/assets/pwa/chrome_1.jpeg";
import iosChromeStep2 from "@/assets/pwa/chrome_2.jpeg";
import iosChromeStep3 from "@/assets/pwa/chrome_3.jpeg";
import iosChromeStep4 from "@/assets/pwa/chrome_4.jpeg";
import chromeLogo from "@/assets/pwa/chrome-logo.png";
import {
  DISMISS_KEY,
  getInstallPlatform,
  isAndroid,
  isIos,
  isStandalone,
  markInstalled,
  shouldAutoPromptInstall,
  writeTs,
} from "@/lib/pwaInstall";

function IosStepVisual({ imageSrc, title }) {
  return (
    <div className="w-full min-w-0">
      <img
        src={imageSrc}
        alt={title}
        className="mx-auto h-auto w-full max-w-[8.5rem] object-contain"
        loading="lazy"
      />
    </div>
  );
}

function InlineStepDetail({ slide }) {
  const Icon = slide?.icon;
  if (!slide) return null;

  if (slide.title.startsWith("1.")) {
    return (
      <span>
        Tryck på <span className="inline-flex translate-y-[2px] items-center"><Icon className="mx-1 h-4 w-4 text-primary" /></span> för att öppna menyn med alternativ.
      </span>
    );
  }

  if (slide.title.startsWith("2.")) {
    return (
      <span>
        Klicka på <span className="inline-flex translate-y-[2px] items-center"><Icon className="mx-1 h-4 w-4 text-primary" /></span> för att öppna alternativen för sidan.
      </span>
    );
  }

  if (slide.title.startsWith("3.")) {
    return (
      <span>
        Klicka på alternativet <span className="inline-flex translate-y-[2px] items-center"><Icon className="mx-1 h-4 w-4 text-primary" /></span> Lägg till på hemskärm.
      </span>
    );
  }

  return <span>{slide.detail}</span>;
}

function AndroidPromoVisual() {
  const chromeHref =
    "https://play.google.com/store/apps/details?id=com.android.chrome";

  return (
    <div className="w-full min-w-0 overflow-hidden rounded-2xl border border-primary/20 bg-[radial-gradient(circle_at_top_left,_rgba(188,31,42,0.18),_transparent_38%),linear-gradient(145deg,hsl(var(--card)),hsl(var(--muted))/0.35)] p-4">
      <div className="mb-4 flex items-center justify-between">
        <Badge className="bg-primary text-primary-foreground">Android</Badge>
        <Sparkles className="h-5 w-5 text-primary" />
      </div>
      <div className="flex flex-col items-center gap-4 text-center">
        <div className="flex h-24 w-full shrink-0 items-center justify-center rounded-3xl bg-background/80 shadow-sm sm:h-28">
          <div className="flex items-center justify-center gap-3 text-primary">
            <TabletSmartphone className="h-9 w-9" />
            <Monitor className="h-9 w-9" />
          </div>
        </div>
        <div className="min-w-0">
          <div className="text-base font-semibold text-foreground sm:text-lg">Installera Lapstr</div>
          <div className="mt-1 text-sm leading-relaxed text-muted-foreground">
            Installera Lapstr appen direkt på din telefon, surfplatta eller dator.
          </div>
          <div className="mt-2 flex flex-col items-center justify-center gap-2 text-xs leading-relaxed text-muted-foreground">
            <img src={chromeLogo} alt="Google Chrome" className="h-8 w-8 shrink-0 object-contain" />
            <span>För bästa upplevelse på Android rekommenderas Google Chrome.</span>
          </div>
          <div className="mt-2 flex justify-center">
            <Button asChild variant="link" size="sm" className="h-auto px-0 text-xs">
              <a href={chromeHref} target="_blank" rel="noreferrer">
                Hämta Chrome i Google Play
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function IosChromePlaceholderVisual() {
  return null;
}

export default function InstallAppPrompt() {
  const deferredPromptRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);
  const [platform, setPlatform] = React.useState(null);
  const [installing, setInstalling] = React.useState(false);
  const [manualOverride, setManualOverride] = React.useState(false);
  const [iosCarouselApi, setIosCarouselApi] = React.useState(null);
  const [iosStepIndex, setIosStepIndex] = React.useState(0);
  const isIosPlatform = platform === "ios_safari" || platform === "ios_chrome" || platform === "ios";

  const maybeOpenPrompt = React.useCallback((nextPlatform, { ignoreCooldown = false, ignoreInstallMarker = false } = {}) => {
    if (!nextPlatform || isStandalone()) return;
    if (!ignoreCooldown && !shouldAutoPromptInstall()) return;
    setManualOverride(Boolean(ignoreInstallMarker || ignoreCooldown));
    setPlatform(nextPlatform);
    setOpen(true);
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const installPlatform = getInstallPlatform();
    if (!installPlatform) {
      return undefined;
    }

    const handleManualOpen = () => {
      if (deferredPromptRef.current) {
        maybeOpenPrompt("android", { ignoreCooldown: true, ignoreInstallMarker: true });
        return;
      }
      if (installPlatform === "ios_safari" || installPlatform === "ios_chrome" || installPlatform === "ios") {
        maybeOpenPrompt(installPlatform, { ignoreCooldown: true, ignoreInstallMarker: true });
        return;
      }
      if (installPlatform === "android") {
        maybeOpenPrompt("android", { ignoreCooldown: true, ignoreInstallMarker: true });
      }
    };

    window.addEventListener("lapstr:open-install-modal", handleManualOpen);

    if (isStandalone()) {
      markInstalled();
      return () => {
        window.removeEventListener("lapstr:open-install-modal", handleManualOpen);
      };
    }

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      deferredPromptRef.current = event;
      maybeOpenPrompt("android");
    };

    const handleAppInstalled = () => {
      markInstalled();
      setOpen(false);
      setManualOverride(false);
      deferredPromptRef.current = null;
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    if (isIos()) {
      const timer = window.setTimeout(() => maybeOpenPrompt(installPlatform || "ios"), 1800);
      return () => {
        window.clearTimeout(timer);
        window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.removeEventListener("appinstalled", handleAppInstalled);
        window.removeEventListener("lapstr:open-install-modal", handleManualOpen);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      window.removeEventListener("lapstr:open-install-modal", handleManualOpen);
    };
  }, [maybeOpenPrompt]);

  React.useEffect(() => {
    if (!isStandalone()) return;
    markInstalled();
  }, []);

  const handleDismiss = React.useCallback(() => {
    writeTs(DISMISS_KEY);
    setOpen(false);
    setManualOverride(false);
  }, []);

  const handleAndroidInstall = React.useCallback(async () => {
    const promptEvent = deferredPromptRef.current;
    if (!promptEvent) {
      return;
    }
    setInstalling(true);
    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      if (choice?.outcome === "accepted") {
        markInstalled();
        setOpen(false);
      } else {
        writeTs(DISMISS_KEY);
        setOpen(false);
      }
      deferredPromptRef.current = null;
    } finally {
      setInstalling(false);
    }
  }, []);

  const iosSlides = React.useMemo(
    () => [
      {
        icon: Ellipsis,
        title: "1. Tryck på mer",
        detail: "Tryck på knappen  för att öppna menyn med alternativ.",
        imageSrc: iosPwaStep1,
      },
      {
        icon: Share,
        title: "2. Tryck på Dela",
        detail: "Klicka på Dela för att öppna alternativen för sidan.",
        imageSrc: iosPwaStep2,
      },
      {
        icon: PlusSquare,
        title: "3. Välj Lägg till",
        detail: "Klicka på alternativet Lägg till på hemskärm.",
        imageSrc: iosPwaStep3,
      },
      {
        icon: Download,
        title: "4. Bekräfta",
        detail: "Tryck Lägg till för att spara Lapstr på hemskärmen.",
        imageSrc: iosPwaStep4,
      },
    ],
    []
  );

  const iosChromeSlides = React.useMemo(
    () => [
      {
        icon: Share,
        title: "1. Tryck på Dela",
        detail: "Tryck på delaknappen i Chrome för att öppna alternativen för sidan.",
        imageSrc: iosChromeStep1,
      },
      {
        icon: Ellipsis,
        title: "2. Tryck på mer",
        detail: "Tryck på knappen med tre prickar för att öppna fler alternativ i Chrome.",
        imageSrc: iosChromeStep2,
      },
      {
        icon: PlusSquare,
        title: "3. Välj Lägg till",
        detail: "Välj Lägg till på hemskärmen i Chrome-menyn.",
        imageSrc: iosChromeStep3,
      },
      {
        icon: Download,
        title: "4. Bekräfta",
        detail: "Bekräfta för att lägga till Lapstr på hemskärmen.",
        imageSrc: iosChromeStep4,
      },
    ],
    []
  );

  React.useEffect(() => {
    if (!iosCarouselApi) return undefined;

    const updateStep = () => {
      setIosStepIndex(iosCarouselApi.selectedScrollSnap());
    };

    updateStep();
    iosCarouselApi.on("select", updateStep);
    iosCarouselApi.on("reInit", updateStep);
    return () => {
      iosCarouselApi.off("select", updateStep);
    };
  }, [iosCarouselApi]);

  const showAndroidManualGuide = platform === "android" && manualOverride && !deferredPromptRef.current;
  const activeIosSlides = platform === "ios_chrome" ? iosChromeSlides : iosSlides;

  if (!platform || isStandalone() || !getInstallPlatform()) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleDismiss();
      }}
    >
      <DialogContent className="w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] overflow-x-hidden border-border p-0 sm:w-[24rem] sm:max-w-[24rem]">
        <div className="p-4 pb-0">
          {isIosPlatform ? (
            <div className="w-full min-w-0 rounded-2xl border border-border/60 bg-gradient-to-br from-card via-card to-primary/5 p-3">
              <div className="mb-4 flex items-center justify-between gap-3">
                <Badge variant="secondary">{platform === "ios_chrome" ? "iPhone + Chrome" : "iPhone"}</Badge>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>Steg {iosStepIndex + 1} av {activeIosSlides.length}</span>
                </div>
              </div>
              <Carousel setApi={setIosCarouselApi} opts={{ align: "start" }} className="w-full min-w-0">
                <CarouselContent className="-ml-0">
                  {activeIosSlides.map((slide) => (
                    <CarouselItem key={slide.title} className="basis-full pl-0">
                      <IosStepVisual imageSrc={slide.imageSrc} title={slide.title} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
              <div className="mt-4 space-y-2 text-center">
                <div className="flex items-center justify-center gap-1.5">
                  {activeIosSlides.map((slide, index) => (
                    <span
                      key={slide.title}
                      aria-hidden="true"
                      className={`h-1.5 rounded-full transition-all ${
                        index === iosStepIndex ? "w-5 bg-primary" : "w-1.5 bg-border"
                      }`}
                    />
                  ))}
                </div>
                
                <div className="flex items-center justify-center gap-2 text-sm font-semibold">
                  {activeIosSlides[iosStepIndex]?.icon ? React.createElement(activeIosSlides[iosStepIndex].icon, { className: "h-4 w-4 text-primary" }) : null}
                  <span>{activeIosSlides[iosStepIndex]?.title}</span>
                </div>
                <div className="text-xs leading-relaxed text-muted-foreground">
                  <InlineStepDetail slide={activeIosSlides[iosStepIndex]} />
                </div>
              </div>
            </div>
          ) : (
            <AndroidPromoVisual />
          )}
        </div>

        <div className="min-w-0 p-5 pt-4">
          {isIosPlatform ? (
            <DialogHeader className="min-w-0 text-left">
              <DialogTitle>Installera Lapstr på din iPhone</DialogTitle>
              <DialogDescription>
                {platform === "ios_chrome"
                  ? "Följ stegen ovan för att lägga till appen på hemskärmen i Google Chrome."
                  : "Följ stegen ovan för att lägga till appen på hemskärmen."}
              </DialogDescription>
            </DialogHeader>
          ) : showAndroidManualGuide ? (
            <DialogHeader className="min-w-0 text-left">
              <DialogTitle>Installera Lapstr på Android</DialogTitle>
              <DialogDescription className="flex items-start gap-2">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  Om webbläsaren inte visar installationsprompten just nu kan du oftast öppna
                  webbläsarens meny och välja <strong>Installera app</strong> eller{" "}
                  <strong>Lägg till på hemskärmen</strong>. I Chrome fungerar detta vanligtvis bäst.
                </span>
              </DialogDescription>
            </DialogHeader>
          ) : platform === "android" && !deferredPromptRef.current ? (
            <DialogHeader className="min-w-0 text-left">
              <DialogDescription className="flex items-start gap-2">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>
                  Det verkar inte gå att installera just nu. Försök igen senare eller använd Chrome på Android om möjligt.
                </span>
              </DialogDescription>
            </DialogHeader>
          ) : null}

          <DialogFooter className="mt-4 gap-2 sm:flex-row sm:justify-between sm:space-x-0">
            <Button className="w-full sm:w-auto" variant="ghost" onClick={handleDismiss} disabled={installing}>
              Inte nu
            </Button>
            {platform === "android" && !showAndroidManualGuide ? (
              <Button
                className="w-full sm:w-auto"
                onClick={handleAndroidInstall}
                disabled={installing || !deferredPromptRef.current}
              >
                Installera appen
              </Button>
            ) : (
              <Button className="w-full sm:w-auto" onClick={handleDismiss}>
                Jag förstår
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
